/**
 * Búsqueda avanzada: borrador del formulario (strings) ↔ cuerpo AdvancedSearch.
 * Ningún campo es obligatorio, pero se exige al menos uno. Las listas (RUT,
 * teléfonos, correos) aceptan varios valores separados por coma o punto y coma.
 */

import type { AdvancedSearch, ParsedQuery } from "../../../shared/contracts.js";

export interface AdvancedDraft {
  keywords: string;
  plate: string;
  vin: string;
  make: string;
  model: string;
  yearFrom: string;
  yearTo: string;
  color: string;
  name: string;
  ruts: string;
  phones: string;
  emails: string;
  region: string;
  comuna: string;
  address: string;
  incidentDate: string;
  daysBefore: string;
  daysAfter: string;
  onlyWindow: boolean;
}

export const EMPTY_ADVANCED: AdvancedDraft = {
  keywords: "", plate: "", vin: "", make: "", model: "", yearFrom: "", yearTo: "", color: "", name: "",
  ruts: "", phones: "", emails: "", region: "", comuna: "", address: "", incidentDate: "", daysBefore: "30",
  daysAfter: "60", onlyWindow: false,
};

export type AdvancedErrors = Partial<Record<keyof AdvancedDraft | "form", string>>;

const list = (s: string): string[] => s.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,190}\.[a-z]{2,24}$/i;

export function toAdvanced(d: AdvancedDraft, now = new Date()):
  { ok: true; value: AdvancedSearch; label: string } | { ok: false; errors: AdvancedErrors } {
  const errors: AdvancedErrors = {};
  const v: AdvancedSearch = {};
  const str = (k: "keywords" | "plate" | "vin" | "make" | "model" | "color" | "name" | "region" | "comuna" | "address") => {
    const x = d[k].trim();
    if (x) v[k] = x;
  };
  (["keywords", "plate", "vin", "make", "model", "color", "name", "region", "comuna", "address"] as const).forEach(str);

  const maxYear = now.getFullYear() + 1;
  const year = (k: "yearFrom" | "yearTo") => {
    const x = d[k].trim();
    if (!x) return;
    const n = Number(x);
    if (!/^\d{4}$/.test(x) || n < 1950 || n > maxYear) errors[k] = `Año entre 1950 y ${maxYear}`;
    else v[k] = n;
  };
  year("yearFrom");
  year("yearTo");

  const ruts = list(d.ruts);
  if (ruts.length > 5) errors.ruts = "Hasta 5 RUT";
  else if (ruts.length) v.ruts = ruts;
  const phones = list(d.phones);
  if (phones.length > 5) errors.phones = "Hasta 5 teléfonos";
  else if (phones.length) v.phones = phones;
  const emails = list(d.emails);
  if (emails.some((e) => !EMAIL.test(e))) errors.emails = "Revise el formato del correo";
  else if (emails.length > 5) errors.emails = "Hasta 5 correos";
  else if (emails.length) v.emails = emails;

  if (d.incidentDate) {
    v.incidentDate = d.incidentDate;
    const days = (k: "daysBefore" | "daysAfter") => {
      const x = d[k].trim();
      if (!x) return;
      const n = Number(x);
      if (!/^\d{1,3}$/.test(x) || n > 365) errors[k] = "Entre 0 y 365 días";
      else v[k] = n;
    };
    days("daysBefore");
    days("daysAfter");
    if (d.onlyWindow) v.onlyWindow = true;
  }

  const label = advancedDraftLabel(d);
  if (!label) errors.form = "Complete al menos un campo.";
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: v, label };
}

/** Etiqueta corta (≤ 200) del formulario; el servidor guarda la versión completa. */
export function advancedDraftLabel(d: AdvancedDraft): string {
  const years = d.yearFrom || d.yearTo ? [d.yearFrom, d.yearTo].filter(Boolean).join("-") : "";
  return [
    d.keywords, d.plate.toUpperCase(), d.vin.toUpperCase(), [d.make, d.model].filter(Boolean).join(" "), years, d.color,
    d.name, d.ruts, d.phones, d.emails, d.comuna || d.region, d.address, d.incidentDate && `siniestro ${d.incidentDate}`,
  ].map((x) => x.trim()).filter(Boolean).join(" · ").slice(0, 200);
}

/** ¿La búsqueda guardada usó campos que solo tiene el formulario avanzado? */
export function isAdvancedParsed(pq: ParsedQuery): boolean {
  return !!(pq.incident || pq.emails?.length || pq.boostText?.length);
}

/** Modelo como se escribió en el campo «Modelo»; el canónico si vino de la caja de texto. */
export function modelTyped(pq: Pick<ParsedQuery, "model" | "raw">): string {
  const m = pq.model;
  if (!m) return "";
  const typed = m.via === "forced" && m.span ? pq.raw.slice(m.span.start, m.span.end).replace(/\s+/g, " ").trim() : "";
  return typed || m.value;
}

/** Reconstruye el formulario desde una búsqueda guardada (reabrir desde el historial). */
export function draftFromParsed(pq: ParsedQuery): AdvancedDraft {
  return {
    ...EMPTY_ADVANCED,
    keywords: pq.freeText.join(" "),
    plate: pq.plates[0]?.plate ?? pq.platePrefix ?? "",
    vin: pq.vin?.value ?? "",
    make: pq.make && pq.make.via !== "inferred" ? pq.make.value : "",
    model: modelTyped(pq),
    yearFrom: pq.year ? String(pq.year.from) : "",
    yearTo: pq.year && pq.year.to !== pq.year.from ? String(pq.year.to) : "",
    color: pq.color ?? "",
    name: [pq.person?.text, ...(pq.persons ?? []).map((o) => o.text)].filter(Boolean).join(", "),
    ruts: pq.ruts.map((r) => `${r.body}-${r.dv}`).join(", "),
    phones: pq.phones.join(", "),
    emails: (pq.emails ?? []).join(", "),
    region: pq.location && !pq.location.comuna ? (pq.location.region ?? "") : "",
    comuna: pq.location?.comuna ?? "",
    address: (pq.boostText ?? []).join(" "),
    incidentDate: pq.incident?.date ?? "",
    daysBefore: String(pq.incident?.daysBefore ?? 30),
    daysAfter: String(pq.incident?.daysAfter ?? 60),
    onlyWindow: pq.incident?.onlyWindow ?? false,
  };
}

/** ¿a y b difieren en una letra (cambiada, sobrante, faltante o dos vecinas invertidas)? */
function oneEditApart(a: string, b: string): boolean {
  if (a === b || Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  const restA = a.slice(i);
  const restB = b.slice(i);
  if (a.length === b.length) {
    return restA.slice(1) === restB.slice(1) || (restA[0] === restB[1] && restA[1] === restB[0] && restA.slice(2) === restB.slice(2));
  }
  return a.length > b.length ? restA.slice(1) === restB : restB.slice(1) === restA;
}

/**
 * Posible error de tipeo entre el correo y el nombre: «igancio.vera90» con el nombre
 * «Ignacio» (una letra de diferencia). Solo avisa: el correo real puede ser así.
 */
export function emailNameTypo(name: string, emails: string): string | null {
  const fold = (s: string): string => s.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase();
  const names = fold(name).split(/[^a-z]+/).filter((w) => w.length >= 4);
  if (!names.length) return null;
  for (const email of list(emails)) {
    const local = fold(email.split("@")[0] ?? "");
    for (const part of local.split(/[^a-z]+/).filter((w) => w.length >= 4)) {
      if (names.includes(part)) continue;
      const near = names.find((n) => oneEditApart(part, n));
      if (near) return `El correo dice «${part}» y el nombre «${near}»: revise si es un error de tipeo.`;
    }
  }
  return null;
}

/** Días entre la fecha del aviso y el siniestro (negativo = antes). */
export function daysFromIncident(seenAt: string, incidentDate: string): number {
  const a = Date.parse(seenAt);
  const b = Date.parse(`${incidentDate}T12:00:00-04:00`);
  return Math.round((a - b) / 86_400_000);
}
